import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  CreatureLimitation, 
} from "@dndModels";
import { 
  CREATURE_TYPES, 
  SIZE_OPTIONS
} from "@dndModels/Constants";

import API from "@utilities/smart_api";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import SelectCreatureBox from "@dndComponents/select/SelectCreatureBox";
import LoadingBox from "@components/input/LoadingBox";
import SuggestionStringBox from "@dndComponents/select/SuggestionStringBox";
import StringsBox from '@components/input/strings/StringsBox';
import StringBox from '@components/input/strings/StringBox';

interface Props { 
  obj: CreatureLimitation;
  change_time?: number;
  onChange: (changed: CreatureLimitation) => void;
  onDelete?: () => void;
  ignore_cr?: boolean;
  disabled?: boolean;
}

const api = API.getInstance();

export default function CreatureLimitInput({ 
  obj,
  change_time = 0,
  onChange,
  onDelete, // I'll add an implementation for this later.
  ignore_cr = false,
  disabled = false,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [cr_options, setCROptions] = useState<number[]>([]);
  const [use_knowledge_key, setUseKnowledgeKey] = useState(obj.knowledge_key !== "");

  const load = useCallback( async () => {
    const res = await api.getObjectFieldValues("creature", "challenge_rating");
    setCROptions(res.map(o => +o));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  useEffect(() => {
    if (prev_change_time !== change_time) {
      setChangeTime(change_time);
    }
  }, [prev_change_time, change_time, setChangeTime]);
  
  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <CheckBox 
            name="Use Knowledge"
            value={use_knowledge_key}
            onChange={(changed) => {
              if (!changed) {
                const new_obj = obj;
                new_obj.knowledge_key = "";
                
                onChange(new_obj);
              }
              setUseKnowledgeKey(changed);
            }}
          />
        </Grid>
        { use_knowledge_key ? 
          <Grid size={12}>
            <StringBox 
              name="Knowledge Key"
              value={obj.knowledge_key} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.knowledge_key = changed;
                
                onChange(new_obj);
              }}
            />
          </Grid>
        :
          <>
            { !ignore_cr &&
              <>
                <Grid size={6}>
                  <SelectStringBox 
                    name="Min Challenge Rating"
                    options={cr_options.filter(o => obj.max_challenge_rating === -1 || o <= obj.max_challenge_rating).map(o => `${o}`)}
                    value={obj.min_challenge_rating === -1 ? `${Math.min(...cr_options)}` : `${obj.min_challenge_rating}`} 
                    disabled={disabled}
                    onChange={(value: string) => {
                      const new_obj = obj;
                      new_obj.min_challenge_rating = +value;
                      
                      onChange(new_obj);
                    }}
                  />
                </Grid>
                <Grid size={6}>
                  <SelectStringBox 
                    name="Max Challenge Rating"
                    options={cr_options.filter(o => o >= obj.min_challenge_rating).map(o => `${o}`)}
                    value={obj.max_challenge_rating === -1 ? `${Math.max(...cr_options)}` : `${obj.max_challenge_rating}`} 
                    disabled={disabled}
                    onChange={(value: string) => {
                      const new_obj = obj;
                      new_obj.max_challenge_rating = +value;
                      
                      onChange(new_obj);
                    }}
                  />
                </Grid>
              </>
            }
            <Grid size={4}>
              <SelectStringBox 
                name="Creature Type" 
                options={CREATURE_TYPES}
                values={obj.creature_types} 
                disabled={disabled}
                multiple
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.creature_types = changed;
                  
                  onChange(new_obj);
                }} 
              />
            </Grid>
            <Grid size={4}>
              <SuggestionStringBox 
                name="Subtype"
                field="subtypes"
                values={obj.subtypes} 
                disabled={disabled}
                multiple
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.subtypes = changed;
                  
                  onChange(new_obj);
                }}
              />
            </Grid>
            <Grid size={4}>
              <StringsBox
                name="Tags"
                values={obj.tags}
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.tags = changed;
                  onChange(new_obj);
                }}
              />
            </Grid>
            <Grid size={6}>
              <SuggestionStringBox 
                name="Age Category"
                field="age_category"
                values={obj.age_categories} 
                disabled={disabled}
                multiple
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.age_categories = changed;
                  
                  onChange(new_obj);
                }}
              />
            </Grid>
            <Grid size={6}>
              <SelectStringBox 
                name="Size"
                options={SIZE_OPTIONS}
                values={obj.size_nums.map(o => `${o}`)} 
                disabled={disabled}
                multiple
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.size_nums = changed.map(o => +o);
                  
                  onChange(new_obj);
                }} 
              />
            </Grid>
            <Grid size={6}>
              <CheckBox 
                name="Allow Flight"
                value={obj.allow_flight} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.allow_flight = changed;
                  
                  onChange(new_obj);
                }} 
              />
            </Grid>
            <Grid size={6}>
              <CheckBox 
                name="Allow Swimming"
                value={obj.allow_swimming} 
                disabled={disabled}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.allow_swimming = changed;
                  
                  onChange(new_obj);
                }} 
              />
            </Grid>
            <Grid size={12}>
              <SelectCreatureBox
                name="Specific Creatures"
                multiple
                values={obj.creature_ids}
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.creature_ids = changed;
                  
                  onChange(new_obj);
                }}
              />
            </Grid>
          </>
        }
      </Grid>
    );
  }
}
